import { Injectable, OnDestroy } from '@angular/core';
import { Subject, Subscription } from 'rxjs';
import { TabToTabChannelName } from '../../enums/tab-to-tab-channel-name.enum';
import { GemviewMessage, GemviewMessageType } from '../../models/gemview.model';
import { TabToTabChannelModel } from '../../models/tab-to-tab-channel.model';
import { TabToTabService } from '../tab-to-tab-messaging/tab-to-tab.service';

/**
 * Service used to send and receive messages between tabs for use with Gemview services.
 */
@Injectable({ providedIn: 'root' })
export class GemviewMessagingService implements OnDestroy {
    /**
     * The channel messages are sent and received on.
     */
    private channel: TabToTabChannelModel;

    /**
     * The subject used to emit messages received from the tab to tab service.
     */
    private subject = new Subject<GemviewMessage<any>>();

    /**
     * The subscribers listening for messages.
     */
    private subscribers: { [k in keyof typeof GemviewMessageType]?: Subscription } = {};

    /**
     * Initializes a new instance of the gemview messaging service.
     *
     * @param tabToTabService The tab to tab service.
     */
    constructor(private tabToTabService: TabToTabService) {
        // Connect to the broadcast channel
        this.channel = this.tabToTabService.connectChannel(TabToTabChannelName.Gemview);
        this.channel.messageSubject$.subscribe({
            next: (result: any) => this.subject.next(result),
            error: (error: any) => this.subject.error(error)
        });
    }

    /**
     * Callback invoked when the service is destroyed.
     */
    public ngOnDestroy(): void {
        this.tabToTabService.disconnectChannel(this.channel);

        for (const subscriber of Object.values(this.subscribers)) {
            subscriber?.unsubscribe();
        }
    }

    /**
     * Subscribes to messages. Unsubscribing is handled for you.
     *
     * @param type The message type to subscribe to.
     * @param next The callback to invoke for success messages.
     * @param error The optional callback to invoke for error messages.
     */
    public subscribe = <T>(
        type: GemviewMessageType,
        next: (result: GemviewMessage<T>) => void,
        error: (error: any) => void = null
    ): void => {
        const subscriber = this.subscribers[type];

        if (!!subscriber) {
            subscriber.unsubscribe();
        }

        this.subscribers[type] = this.subject.subscribe({
            error,
            next: (message) => {
                if (message.type == type) {
                    next(message);
                }
            }
        });
    };

    /**
     * Sends a message to all subscribers.
     *
     * @param message The message to send.
     * @param type The type of message to send.
     */
    public send = <T>(data: T, type: GemviewMessageType): void => {
        const message: GemviewMessage<T> = { type, data };
        this.tabToTabService.sendMessage(TabToTabChannelName.Gemview, message);
    };
}
